<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="loadingText" />
  </div>
  <div v-else class="edit-transaction-view">
    <MainHeader :title="'Transaction'">
      <template #left>
        <font-awesome-icon
          @click="router.push({ name: 'Transactions' })"
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          aria-label="Return to transactions view"
        />
      </template>
    </MainHeader>

    <div class="view et-view">
      <div v-if="isSplitChild" class="split-child-banner">
        <font-awesome-icon :icon="['fas', 'circle-info']" class="banner-icon" />
        <div class="banner-text">
          <div class="banner-title">Part of a split transaction</div>
          <div class="banner-subtitle">
            This was split from
            <span class="banner-parent-name">{{
              localEditTransaction.parentDisplayName || 'another transaction'
            }}</span>
          </div>
        </div>
        <button
          class="banner-link"
          @click="goToParentSplit"
          aria-label="View original transaction"
        >
          View original
        </button>
      </div>

      <div v-if="isSplitParent" class="split-parent-banner">
        <font-awesome-icon :icon="['fas', 'code-fork']" class="banner-icon" />
        <div class="banner-text">
          <div class="banner-title">
            Split into {{ localEditTransaction.splitChildCount || '?' }}
            transactions
          </div>
          <div class="banner-subtitle">
            Original amount allocated across
            {{ localEditTransaction.splitChildCount || 'multiple' }} categories
          </div>
        </div>
      </div>

      <div class="et-tabs">
        <button
          class="et-tab"
          :class="{ active: activeTab === 'info' }"
          @click="activeTab = 'info'"
        >
          Info
        </button>
        <button
          class="et-tab"
          :class="{ active: activeTab === 'edit' }"
          @click="activeTab = 'edit'"
        >
          Edit
        </button>
        <button
          class="et-tab"
          :class="{ active: activeTab === 'split' }"
          :disabled="isSplitChild"
          :title="isSplitChild ? 'Split children cannot be re-split' : ''"
          @click="activeTab = 'split'"
        >
          Split
        </button>
      </div>

      <!-- ==================== INFO TAB ==================== -->
      <div v-if="activeTab === 'info'">
        <div class="et-amount-display">
          <span class="et-amount-label">Amount</span>
          <span
            class="et-amount-value"
            :class="
              localEditTransaction.isIncome ? 'amount-income' : 'amount-neutral'
            "
          >
            {{ formatMoney(localEditTransaction.amount) }}
          </span>
        </div>

        <div class="wrapper-card et-info-card">
          <div class="ud-section">
            <span class="section-label">Transaction details</span>

            <div class="et-info-row">
              <span class="et-info-label">Name</span>
              <span class="et-info-value">{{
                localEditTransaction.displayName
              }}</span>
            </div>
            <div class="et-info-row">
              <span class="et-info-label">Description</span>
              <span class="et-info-value">{{
                localEditTransaction.displayDescription || 'N/A'
              }}</span>
            </div>
            <div class="et-info-row">
              <span class="et-info-label">Bank Account</span>
              <span class="et-info-value">{{
                localEditTransaction.accountDisplayName || 'N/A'
              }}</span>
            </div>
            <div class="et-info-row">
              <span class="et-info-label">Date</span>
              <span class="et-info-value">{{
                formattedDate(localEditTransaction.transactionDate)
              }}</span>
            </div>
          </div>

          <div class="ud-section">
            <span class="section-label">Category</span>
            <div class="et-info-row">
              <span class="et-info-label">Category</span>
              <span class="et-info-value">
                {{
                  localEditTransaction.transactionCategory?.name ||
                  'Uncategorized'
                }}
              </span>
            </div>
            <div
              v-if="localEditTransaction.transactionSubcategory"
              class="et-info-row"
            >
              <span class="et-info-label">Subcategory</span>
              <span class="et-info-value">{{
                localEditTransaction.transactionSubcategory.name || 'N/A'
              }}</span>
            </div>
          </div>

          <div class="ud-section">
            <span class="section-label">Flags</span>
            <div class="et-info-row">
              <span class="et-info-label">Transfer</span>
              <span
                class="et-flag"
                :class="
                  localEditTransaction.markedAsTransfer ? 'flag-on' : 'flag-off'
                "
              >
                {{ localEditTransaction.markedAsTransfer ? 'Yes' : 'No' }}
              </span>
            </div>
            <div class="et-info-row">
              <span class="et-info-label">Hidden</span>
              <span
                class="et-flag"
                :class="localEditTransaction.hidden ? 'flag-on' : 'flag-off'"
              >
                {{ localEditTransaction.hidden ? 'Yes' : 'No' }}
              </span>
            </div>
            <div class="et-info-row">
              <span class="et-info-label">Money In</span>
              <span
                class="et-flag"
                :class="
                  localEditTransaction.isIncome ? 'flag-income' : 'flag-off'
                "
              >
                {{ localEditTransaction.isIncome ? 'Yes' : 'No' }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="isSplitChild" class="split-child-banner mt-3">
          <font-awesome-icon
            :icon="['fas', 'circle-info']"
            class="banner-icon"
          />
          <div class="banner-text">
            <div class="banner-title">
              To edit or delete this transaction you need to go to the original
              split transaction
            </div>
          </div>
          <button
            class="banner-link"
            @click="goToParentSplit"
            aria-label="View original transaction"
          >
            View original
          </button>
        </div>

        <button
          class="delete-btn mt-3 w-100"
          aria-label="Delete Transaction"
          :disabled="isSplitChild"
          :title="
            isSplitChild
              ? 'To delete this, unsplit the original transaction first'
              : ''
          "
          @click="onDeleteClick"
        >
          Delete Transaction
        </button>
      </div>

      <!-- ==================== EDIT TAB ==================== -->
      <div v-else-if="activeTab === 'edit'">
        <form @submit.prevent="editTransaction">
          <fieldset>
            <div class="ud-section">
              <span class="section-label">Details</span>

              <div class="ud-field">
                <label class="ud-label" for="transactionName"
                  >Name <span class="ud-asterisk">*</span></label
                >
                <input
                  id="transactionName"
                  type="text"
                  class="ud-input"
                  v-model="editName"
                  required
                />
              </div>

              <div class="ud-field mt">
                <label class="ud-label" for="transactionDescription"
                  >Description</label
                >
                <input
                  id="transactionDescription"
                  type="text"
                  class="ud-input"
                  v-model="editDescription"
                />
              </div>

              <div class="ud-field mt">
                <label class="ud-label" for="transactionAmount">
                  Amount
                  <span class="ud-asterisk">*</span>
                  <span v-if="isSplitChild" class="ud-hint">
                    — locked because this is part of a split
                  </span>
                  <span v-else-if="isSplitParent" class="ud-hint">
                    — locked because this transaction has been split
                  </span>
                </label>
                <input
                  id="transactionAmount"
                  type="text"
                  class="ud-input"
                  v-model="editAmount"
                  :disabled="isSplitChild || isSplitParent"
                  required
                />
              </div>

              <div class="ud-field mt">
                <label class="ud-label" for="editDate">Date</label>
                <input
                  id="editDate"
                  type="date"
                  class="ud-input date-picker"
                  v-model="editDate"
                />
              </div>
            </div>

            <div class="ud-section">
              <span class="section-label">Category</span>

              <div class="ud-field">
                <label class="ud-label" for="transactionCategory"
                  >Category</label
                >
                <select
                  v-model="editParentCategory"
                  class="ud-input ud-select form-select"
                  @change="selectedEditParentCategory"
                >
                  <option :value="null" disabled>Select category</option>
                  <option
                    v-for="parentCategory in store.categories"
                    :key="parentCategory.categoryID"
                    :value="parentCategory"
                  >
                    {{ parentCategory.name }}
                  </option>
                </select>
              </div>

              <div
                v-if="
                  editParentCategory && editParentCategory.subcategories != null
                "
                class="ud-field mt"
              >
                <label class="ud-label" for="subcategory">Subcategory</label>
                <select
                  v-model="editSubcategory"
                  class="ud-input ud-select form-select"
                >
                  <option :value="null">None</option>
                  <option
                    v-for="subcategory in editParentCategory.subcategories"
                    :key="subcategory.subcategoryID"
                    :value="subcategory"
                  >
                    {{ subcategory.name }}
                  </option>
                </select>
              </div>

              <div
                v-if="editParentCategory && !isSplitChild"
                class="ud-field mt"
              >
                <div v-if="existingMatchingRule" class="rule-exists-pill">
                  <font-awesome-icon :icon="['fas', 'circle-check']" />
                  <span>
                    "{{ editName }}" is already auto-categorized as
                    {{
                      editSubcategory
                        ? editSubcategory.name
                        : editParentCategory.name
                    }}
                  </span>
                </div>

                <button
                  v-else
                  type="button"
                  class="create-rule-btn"
                  @click="createAutocategorizationRule"
                >
                  <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" />
                  Auto-categorize future "{{ editName }}" transactions
                </button>
              </div>
            </div>

            <div class="ud-section">
              <span class="section-label">Flags</span>

              <div class="toggle-row">
                <span class="ud-label">Mark as transfer</span>
                <Toggle
                  id="markedAsTransfer"
                  :toggleActive="editMarkedAsTransfer"
                  @toggleSelected="toggleTransfer($event)"
                />
              </div>
              <div class="toggle-row">
                <span class="ud-label">Hide transaction</span>
                <Toggle
                  id="hidden"
                  :toggleActive="editHidden"
                  @toggleSelected="toggleHidden($event)"
                />
              </div>
              <div class="toggle-row">
                <span class="ud-label">Money in (deposit / income)</span>
                <Toggle
                  id="isIncome"
                  :toggleActive="editIncome"
                  @toggleSelected="toggleDeposit($event)"
                />
              </div>
            </div>

            <button
              class="ud-submit"
              aria-label="submit edit transaction"
              role="button"
              type="submit"
            >
              Update Transaction
            </button>
          </fieldset>
        </form>
      </div>

      <!-- ==================== SPLIT TAB ==================== -->
      <div v-else-if="activeTab === 'split'">
        <div v-if="isSplitParent">
          <div class="split-intro">
            <p class="split-intro-text">
              This transaction was split into
              <strong>{{
                splitChildren.length || localEditTransaction.splitChildCount
              }}</strong>
              transactions. Tap any row to edit it, or unsplit to restore the
              original.
            </p>
          </div>

          <div v-if="loadingChildren" class="split-children-loading">
            Loading split details...
          </div>

          <div v-else class="split-children-list">
            <div
              v-for="child in splitChildren"
              :key="child.userTransactionID"
              class="split-child-row wrapper-card"
              @click="openChild(child)"
              role="button"
              :aria-label="`View split: ${child.displayName}`"
            >
              <div class="split-child-icon">
                <font-awesome-icon
                  :icon="
                    child.transactionCategory?.icon
                      ? parseIconString(child.transactionCategory.icon)
                      : ['fas', 'tag']
                  "
                  :style="{
                    color: child.transactionCategory?.iconColor || '#86c24c',
                  }"
                />
              </div>
              <div class="split-child-info">
                <div class="split-child-name">{{ child.displayName }}</div>
                <div class="split-child-category">
                  {{
                    child.transactionSubcategory?.name ||
                    child.transactionCategory?.name ||
                    'Uncategorized'
                  }}
                </div>
              </div>
              <div class="split-child-amount">
                {{ formatMoney(child.amount) }}
              </div>
            </div>
          </div>

          <div class="split-actions-row">
            <button
              type="button"
              class="unsplit-btn"
              @click="onUnsplitClick"
              aria-label="Unsplit this transaction"
            >
              <font-awesome-icon :icon="['fas', 'rotate-left']" /> Unsplit
            </button>
            <button
              type="button"
              class="resplit-btn"
              @click="onResplitClick"
              aria-label="Re-split this transaction"
            >
              <font-awesome-icon :icon="['fas', 'arrows-rotate']" /> Re-split
            </button>
          </div>
        </div>

        <div v-else>
          <div class="split-intro">
            <p class="split-intro-text">
              Split this transaction across multiple categories. The amounts
              must add up to
              <strong>{{ formatMoney(localEditTransaction.amount) }}</strong
              >.
            </p>
          </div>

          <div class="quick-split-row">
            <span class="quick-split-label">Quick split:</span>
            <button type="button" class="quick-split-btn" @click="evenSplit(2)">
              ½
            </button>
            <button type="button" class="quick-split-btn" @click="evenSplit(3)">
              ⅓
            </button>
            <button type="button" class="quick-split-btn" @click="evenSplit(4)">
              ¼
            </button>
            <button
              type="button"
              class="quick-split-btn quick-split-btn-secondary"
              @click="distributeRemainder"
              :disabled="splits.length < 2"
            >
              Auto-fill remainder
            </button>
          </div>

          <div class="splits-list">
            <div
              v-for="(split, index) in splits"
              :key="split._key"
              class="split-card wrapper-card"
            >
              <div class="split-card-header">
                <span class="split-card-title">Split {{ index + 1 }}</span>
                <button
                  type="button"
                  class="split-remove-btn"
                  @click="removeSplit(index)"
                  :disabled="splits.length <= 2"
                  :title="
                    splits.length <= 2 ? 'Minimum 2 splits required' : 'Remove'
                  "
                >
                  <font-awesome-icon :icon="['fas', 'xmark']" />
                </button>
              </div>

              <div class="ud-field">
                <label class="ud-label">Name</label>
                <input
                  type="text"
                  class="ud-input"
                  v-model="split.displayName"
                  :placeholder="localEditTransaction.displayName"
                />
              </div>

              <div class="ud-field mt">
                <label class="ud-label">Description</label>
                <input
                  type="text"
                  class="ud-input"
                  v-model="split.displayDescription"
                  placeholder="Optional"
                />
              </div>

              <div class="ud-field mt">
                <label class="ud-label">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  class="ud-input"
                  v-model.number="split.amount"
                  placeholder="0.00"
                />
              </div>

              <div class="ud-field mt">
                <label class="ud-label">Category</label>
                <select
                  v-model="split.category"
                  class="ud-input ud-select form-select"
                  @change="onSplitCategoryChange(index)"
                >
                  <option :value="null" disabled>Select category</option>
                  <option
                    v-for="cat in store.categories"
                    :key="cat.categoryID"
                    :value="cat"
                  >
                    {{ cat.name }}
                  </option>
                </select>
              </div>

              <div
                v-if="split.category && split.category.subcategories"
                class="ud-field mt"
              >
                <label class="ud-label">Subcategory</label>
                <select
                  v-model="split.subcategory"
                  class="ud-input ud-select form-select"
                >
                  <option :value="null">None</option>
                  <option
                    v-for="sub in split.category.subcategories"
                    :key="sub.subcategoryID"
                    :value="sub"
                  >
                    {{ sub.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <button type="button" class="add-split-btn" @click="addSplit">
            <font-awesome-icon :icon="['fas', 'plus']" /> Add another split
          </button>

          <div class="split-totals" role="status" aria-live="polite">
            <div class="split-totals-row">
              <span class="split-totals-label">Original amount</span>
              <span class="split-totals-value">{{
                formatMoney(localEditTransaction.amount)
              }}</span>
            </div>
            <div class="split-totals-row">
              <span class="split-totals-label">Allocated</span>
              <span class="split-totals-value">{{
                formatMoney(splitsSum)
              }}</span>
            </div>
            <div
              class="split-totals-row split-totals-remaining"
              :class="{
                'remaining-balanced': isBalanced,
                'remaining-off': !isBalanced,
              }"
            >
              <span class="split-totals-label">Remaining</span>
              <span class="split-totals-value">{{
                formatMoney(splitsRemaining)
              }}</span>
            </div>
          </div>

          <div v-if="splitValidationMessage" class="split-validation">
            <font-awesome-icon
              :icon="['fas', 'triangle-exclamation']"
              class="validation-icon"
            />
            {{ splitValidationMessage }}
          </div>

          <button
            type="button"
            class="ud-submit"
            :disabled="!canSubmitSplit"
            @click="submitSplit"
          >
            Apply split
          </button>
        </div>
      </div>
    </div>
  </div>

  <Modal ref="deleteSplitParentModal">
    <template #header>
      <h2>Delete split transaction?</h2>
    </template>
    <template #body>
      <p>
        This will also delete the
        <strong>{{ localEditTransaction.splitChildCount || '' }}</strong>
        split
        {{
          localEditTransaction.splitChildCount === 1
            ? 'transaction'
            : 'transactions'
        }}
        you created from it.
      </p>
      <p class="mt-half">This action cannot be undone.</p>
    </template>
    <template #footer>
      <div class="modal-footer-buttons">
        <button class="teal-outline-button" @click="cancelDeleteSplitParent">
          Cancel
        </button>
        <button class="red-button" @click="confirmDeleteSplitParent">
          Delete all
        </button>
      </div>
    </template>
  </Modal>

  <Modal ref="unsplitModal">
    <template #header>
      <h2>
        {{
          unsplitMode === 'resplit'
            ? 'Re-split this transaction?'
            : 'Unsplit this transaction?'
        }}
      </h2>
    </template>
    <template #body>
      <p>
        This will remove the
        <strong>{{
          splitChildren.length || localEditTransaction.splitChildCount
        }}</strong>
        split
        {{
          (splitChildren.length || localEditTransaction.splitChildCount) === 1
            ? 'transaction'
            : 'transactions'
        }}
        and restore the original.
      </p>
      <p class="mt-half" v-if="unsplitMode === 'resplit'">
        You'll then be able to split it again with new categories or amounts.
      </p>
    </template>
    <template #footer>
      <div class="modal-footer-buttons">
        <button class="teal-outline-button" @click="cancelUnsplit">
          Cancel
        </button>
        <button class="teal-button" @click="confirmUnsplit">
          {{ unsplitMode === 'resplit' ? 'Yes, re-split' : 'Yes, unsplit' }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script>
import { useMyStore } from '../store';
import { useRouter } from 'vue-router';
import { onMounted, ref, watch, computed } from 'vue';
import MainHeader from '../components/View-Header.vue';
import Toggle from '../components/Toggle.vue';
import { formatMoney } from '../utils/formatMoney.js';
import { formatDateRelative } from '../utils/formatDate.js';
import SkeletonLoader from '../components/SkeletonLoader.vue';
import Modal from '../components/Main-Modal.vue';

export default {
  components: { MainHeader, Toggle, SkeletonLoader, Modal },
  setup() {
    const store = useMyStore();
    const router = useRouter();

    const activeTab = ref('info');

    let localEditTransaction = ref({});
    let editName = ref();
    let editDescription = ref();
    let editAmount = ref();
    let editDate = ref();
    let editParentCategory = ref(null);
    let editSubcategory = ref(null);
    let editMarkedAsTransfer = ref();
    let editHidden = ref();
    const loading = ref(true);
    let loadingText = ref('Loading...');
    const editIncome = ref();
    const categoryRulesForSelected = ref([]);

    const deleteSplitParentModal = ref(null);
    const unsplitModal = ref(null);
    const unsplitMode = ref('unsplit');
    const splitChildren = ref([]);
    const loadingChildren = ref(false);

    const parseIconString = (iconString) => {
      if (iconString) {
        const [category, icon] = iconString.split(' ');
        return [category, icon];
      }
      return null;
    };

    const loadSplitChildren = async () => {
      if (!localEditTransaction.value?.userTransactionID) return;
      loadingChildren.value = true;
      try {
        const response = await store.getSplitChildren(
          localEditTransaction.value.userTransactionID
        );
        if (response?.status === 200) {
          splitChildren.value = response.data || [];
        } else {
          splitChildren.value = [];
        }
      } catch (e) {
        console.error('Failed to load split children:', e);
        splitChildren.value = [];
      } finally {
        loadingChildren.value = false;
      }
    };

    const openChild = (child) => {
      router.push({
        name: 'EditTransaction',
        params: { userTransactionID: child.userTransactionID },
      });
    };

    const performDelete = () => {
      loadingText.value = 'Deleting Transaction';
      loading.value = true;
      store
        .deleteTransaction(localEditTransaction.value.userTransactionID)
        .then(() => {
          loading.value = false;
          router.push({ name: 'Transactions' });
        });
    };

    const onDeleteClick = () => {
      if (isSplitParent.value) {
        deleteSplitParentModal.value?.openModal();
      } else {
        performDelete();
      }
    };

    const confirmDeleteSplitParent = () => {
      deleteSplitParentModal.value?.closeModal();
      performDelete();
    };

    const cancelDeleteSplitParent = () => {
      deleteSplitParentModal.value?.closeModal();
    };

    const onUnsplitClick = () => {
      unsplitMode.value = 'unsplit';
      unsplitModal.value?.openModal();
    };

    const onResplitClick = () => {
      unsplitMode.value = 'resplit';
      unsplitModal.value?.openModal();
    };

    const cancelUnsplit = () => {
      unsplitModal.value?.closeModal();
    };

    const confirmUnsplit = async () => {
      unsplitModal.value?.closeModal();
      const wasResplit = unsplitMode.value === 'resplit';
      loadingText.value = wasResplit
        ? 'Re-splitting Transaction'
        : 'Unsplitting Transaction';
      loading.value = true;

      try {
        const response = await store.unsplitTransaction(
          localEditTransaction.value.userTransactionID
        );

        if (response?.status === 200) {
          const tx = await store.getTransactionData(
            localEditTransaction.value.userTransactionID
          );
          localEditTransaction.value =
            typeof tx.data === 'string' ? JSON.parse(tx.data) : tx.data;
          splitChildren.value = [];
          splits.value = [];

          if (wasResplit) {
            loading.value = false;
            activeTab.value = 'split';
            initSplits();
          } else {
            loading.value = false;
            router.push({ name: 'Transactions' });
          }
        } else {
          loading.value = false;
          alert(
            response?.data?.errorMessage ||
              'Could not unsplit this transaction. Please try again.'
          );
        }
      } catch (e) {
        loading.value = false;
        console.error('Unsplit error:', e);
      }
    };

    const splits = ref([]);
    let splitKeyCounter = 0;
    const makeSplitKey = () => `split-${splitKeyCounter++}`;

    const isSplitParent = computed(() => !!localEditTransaction.value?.isSplit);
    const isSplitChild = computed(
      () => !!localEditTransaction.value?.parentTransactionID
    );

    const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

    const splitsSum = computed(() =>
      round2(splits.value.reduce((acc, s) => acc + (Number(s.amount) || 0), 0))
    );

    const splitsRemaining = computed(() =>
      round2(
        (Number(localEditTransaction.value?.amount) || 0) - splitsSum.value
      )
    );

    const isBalanced = computed(() => splitsRemaining.value === 0);

    const allSplitsHaveAmount = computed(() =>
      splits.value.every(
        (s) => s.amount !== null && s.amount !== undefined && s.amount !== ''
      )
    );

    const allSplitsHaveCategory = computed(() =>
      splits.value.every((s) => !!s.category)
    );

    const splitValidationMessage = computed(() => {
      if (splits.value.length < 2) return 'You need at least 2 splits.';
      if (!allSplitsHaveAmount.value) return 'Every split needs an amount.';
      if (!allSplitsHaveCategory.value) return 'Every split needs a category.';
      if (!isBalanced.value) {
        const remaining = splitsRemaining.value;
        if (remaining > 0)
          return `Splits don't add up — ${formatMoney(
            remaining
          )} still needs to be allocated.`;
        return `Splits exceed the original by ${formatMoney(
          Math.abs(remaining)
        )}.`;
      }
      return '';
    });

    const canSubmitSplit = computed(() => splitValidationMessage.value === '');

    const initSplits = () => {
      splits.value = [
        {
          _key: makeSplitKey(),
          displayName: localEditTransaction.value?.displayName || '',
          displayDescription: '',
          amount: null,
          category: editParentCategory.value || null,
          subcategory: editSubcategory.value || null,
        },
        {
          _key: makeSplitKey(),
          displayName: localEditTransaction.value?.displayName || '',
          displayDescription: '',
          amount: null,
          category: null,
          subcategory: null,
        },
      ];
    };

    const addSplit = () => {
      splits.value.push({
        _key: makeSplitKey(),
        displayName: localEditTransaction.value?.displayName || '',
        displayDescription: '',
        amount: null,
        category: null,
        subcategory: null,
      });
    };

    const removeSplit = (index) => {
      if (splits.value.length <= 2) return;
      splits.value.splice(index, 1);
    };

    const onSplitCategoryChange = (index) => {
      splits.value[index].subcategory = null;
    };

    const evenSplit = (n) => {
      const total = Number(localEditTransaction.value?.amount) || 0;
      const cents = Math.round(total * 100);
      const base = Math.trunc(cents / n);
      const remainder = cents - base * n;

      while (splits.value.length < n) addSplit();
      while (splits.value.length > n) splits.value.pop();

      splits.value.forEach((s, i) => {
        const portionCents = i === n - 1 ? base + remainder : base;
        s.amount = round2(portionCents / 100);
      });
    };

    const distributeRemainder = () => {
      const remaining = splitsRemaining.value;
      if (remaining === 0) return;
      let target = -1;
      for (let i = splits.value.length - 1; i >= 0; i--) {
        const a = splits.value[i].amount;
        if (a === null || a === undefined || a === '' || Number(a) === 0) {
          target = i;
          break;
        }
      }
      if (target === -1) target = splits.value.length - 1;
      splits.value[target].amount = round2(
        (Number(splits.value[target].amount) || 0) + remaining
      );
    };

    const submitSplit = async () => {
      if (!canSubmitSplit.value) return;
      loadingText.value = 'Splitting Transaction';
      loading.value = true;

      const payload = {
        parentTransactionID: localEditTransaction.value.userTransactionID,
        splits: splits.value.map((s) => ({
          displayName:
            s.displayName?.trim() || localEditTransaction.value.displayName,
          displayDescription: s.displayDescription || null,
          amount: round2(s.amount),
          transactionCategoryID: s.category?.categoryID || null,
          transactionSubcategoryID: s.subcategory?.subcategoryID || null,
        })),
      };

      try {
        const response = await store.splitTransaction(payload);

        if (response && (response.status === 200 || response.status === 201)) {
          router.push({ name: 'Transactions' });
          return;
        }
        loading.value = false;
        const msg =
          response?.data?.errorMessage ||
          'Could not split this transaction. Please try again.';
        console.error('Split failed:', msg);
        alert(msg);
      } catch (error) {
        loading.value = false;
        console.error('Split error:', error);
      }
    };

    const formattedDate = (dateString) => {
      const date = new Date(dateString);
      const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
      return date.toLocaleDateString('en-US', options);
    };

    const formattedDateForInput = (inputDate) => {
      const date = new Date(inputDate);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const selectedEditParentCategory = () => {
      editSubcategory.value = null;
    };

    const existingMatchingRule = computed(() => {
      if (!categoryRulesForSelected.value?.length) return null;
      if (!editName.value) return null;

      const txnName = editName.value.toLowerCase();

      return categoryRulesForSelected.value.find((rule) => {
        if (rule.type !== 'Name') return false;
        const ruleValue = rule.comparisonValue?.toLowerCase();
        if (!ruleValue) return false;

        if (rule.comparison === 'equals') {
          return txnName === ruleValue;
        }
        if (rule.comparison === 'contains') {
          return txnName.includes(ruleValue);
        }
        return false;
      });
    });

    const createAutocategorizationRule = async () => {
      if (!editParentCategory.value || !editName.value) return;

      const existingRules = categoryRulesForSelected.value.map((r) => ({
        Type: r.type,
        Comparison: r.comparison,
        ComparisonValue: r.comparisonValue,
      }));

      const newRule = {
        Type: 'Name',
        Comparison: 'equals',
        ComparisonValue: editName.value,
      };

      const payload = {
        CategoryID: editParentCategory.value.categoryID,
        SubcategoryID: editSubcategory.value?.subcategoryID || null,
        CategoryRules: [...existingRules, newRule],
      };

      try {
        await store.saveCategoryRules(payload);

        const updatedRules = await store.getCategoryRulesForTransaction({
          categoryID: editParentCategory.value.categoryID,
          subcategoryID: editSubcategory.value?.subcategoryID,
        });
        categoryRulesForSelected.value = updatedRules || [];
      } catch (error) {
        console.error('Could not create rule', error);
      }
    };

    const editTransaction = () => {
      loadingText.value = 'Updating Transaction';
      loading.value = true;
      const updatedTransaction = {
        userTransactionID: localEditTransaction.value.userTransactionID,
        isEdited: true,
        transactionCategoryID: editParentCategory.value
          ? editParentCategory.value.categoryID
          : null,
        transactionSubcategoryID:
          editSubcategory && editSubcategory.value
            ? editSubcategory.value.subcategoryID
            : null,
        displayName: editName.value,
        displayDescription: editDescription.value,
        transactionDate: editDate.value
          ? new Date(editDate.value + 'T00:00:00').toISOString()
          : null,
        amount: editAmount.value,
        markedAsTransfer: editMarkedAsTransfer.value,
        hidden: editHidden.value,
        transactionDeleted: false,
        isIncome: editIncome.value,
      };

      store
        .updateTransaction(updatedTransaction)
        .then((response) => {
          if (response.status == 200) {
            loading.value = false;
            router.push({ name: 'Transactions' });
          }
        })
        .catch((error) => {
          loading.value = false;
          console.error('An error occurred:', error);
        });
    };

    const deleteTransaction = () => {
      loadingText.value = 'Deleting Transaction';
      loading.value = true;
      store
        .deleteTransaction(localEditTransaction.value.userTransactionID)
        .then(() => {
          loading.value = false;
          router.push({ name: 'Transactions' });
        });
    };

    const goToParentSplit = () => {
      const parentID = localEditTransaction.value?.parentTransactionID;
      if (!parentID) return;
      router.push({
        name: 'EditTransaction',
        params: { userTransactionID: parentID },
      });
    };

    const toggleHidden = (event) => {
      editHidden.value = event;
    };
    const toggleTransfer = (event) => {
      editMarkedAsTransfer.value = event;
    };
    const toggleDeposit = (event) => {
      editIncome.value = event;
    };

    onMounted(async () => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) mainElement.scrollTo(0, 0);
      }, 100);

      try {
        if (!store.categories || store.categories.length === 0) {
          await store.getAllCategories();
        }

        const response = await store.getTransactionData(
          router.currentRoute.value.params.userTransactionID
        );
        localEditTransaction.value =
          typeof response.data === 'string'
            ? JSON.parse(response.data)
            : response.data;
      } catch (error) {
        console.log('Load error: ' + JSON.stringify(error));
      } finally {
        loading.value = false;
      }
    });

    watch(
      [editParentCategory, editSubcategory],
      async ([newCategory, newSubcategory]) => {
        if (!newCategory) {
          categoryRulesForSelected.value = [];
          return;
        }

        try {
          const rules = await store.getCategoryRulesForTransaction({
            categoryID: newCategory.categoryID,
            subcategoryID: newSubcategory?.subcategoryID,
          });
          categoryRulesForSelected.value = rules || [];
        } catch (error) {
          console.error('Could not fetch rules for category', error);
          categoryRulesForSelected.value = [];
        }
      }
    );

    watch(
      localEditTransaction,
      (newValue) => {
        if (newValue) {
          editName.value = localEditTransaction.value.displayName;
          editDescription.value = localEditTransaction.value.displayDescription;
          editAmount.value = localEditTransaction.value.amount;
          editDate.value = formattedDateForInput(
            localEditTransaction.value.transactionDate
          );

          const matchingCategory = store.categories.find(
            (category) =>
              category.categoryID ===
              localEditTransaction.value?.transactionCategory?.categoryID
          );

          if (matchingCategory) {
            editParentCategory.value = matchingCategory;
            if (
              editParentCategory.value.subcategories != null &&
              localEditTransaction.value?.transactionSubcategory
            ) {
              editSubcategory.value =
                editParentCategory.value.subcategories.find(
                  (subcategory) =>
                    subcategory.subcategoryID ===
                    localEditTransaction.value?.transactionSubcategory
                      ?.subcategoryID
                );
            } else {
              editSubcategory.value = null;
            }
          } else {
            editParentCategory.value = null;
            editSubcategory.value = null;
          }

          editMarkedAsTransfer.value =
            localEditTransaction.value.markedAsTransfer;
          editHidden.value = localEditTransaction.value.hidden;
          editIncome.value = localEditTransaction.value.isIncome;

          if (splits.value.length === 0) {
            initSplits();
          }
        }
      },
      { deep: true }
    );

    watch(activeTab, (tab) => {
      if (tab === 'split') {
        if (isSplitParent.value) {
          loadSplitChildren();
        } else if (splits.value.length === 0) {
          initSplits();
        }
      }
    });

    watch(
      () => router.currentRoute.value.params.userTransactionID,
      (newId, oldId) => {
        if (
          router.currentRoute.value.name !== 'EditTransaction' ||
          !newId ||
          newId === oldId ||
          newId === String(localEditTransaction.value?.userTransactionID)
        ) {
          return;
        }
        loading.value = true;
        splits.value = [];
        activeTab.value = 'info';
        store.getTransactionData(newId).then((response) => {
          loading.value = false;
          localEditTransaction.value =
            typeof response.data === 'string'
              ? JSON.parse(response.data)
              : response.data;
        });
      }
    );

    return {
      store,
      router,
      activeTab,
      formattedDate,
      formatMoney,
      formatDateRelative,
      localEditTransaction,
      editName,
      editDescription,
      editAmount,
      editDate,
      editParentCategory,
      editTransaction,
      formattedDateForInput,
      editMarkedAsTransfer,
      editHidden,
      toggleHidden,
      toggleTransfer,
      deleteTransaction,
      editSubcategory,
      selectedEditParentCategory,
      loading,
      loadingText,
      createAutocategorizationRule,
      toggleDeposit,
      editIncome,
      splits,
      isSplitParent,
      isSplitChild,
      splitsSum,
      splitsRemaining,
      isBalanced,
      splitValidationMessage,
      canSubmitSplit,
      addSplit,
      removeSplit,
      onSplitCategoryChange,
      evenSplit,
      distributeRemainder,
      submitSplit,
      goToParentSplit,
      deleteSplitParentModal,
      unsplitModal,
      unsplitMode,
      splitChildren,
      loadingChildren,
      parseIconString,
      openChild,
      onDeleteClick,
      confirmDeleteSplitParent,
      cancelDeleteSplitParent,
      onUnsplitClick,
      onResplitClick,
      cancelUnsplit,
      confirmUnsplit,
      categoryRulesForSelected,
      existingMatchingRule,
    };
  },
};
</script>

<style scoped>
.et-view {
  max-width: 480px;
  margin: 0 auto;
}

.split-child-banner,
.split-parent-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.85rem 1rem;
  border-radius: var(--radius-md);
  margin-bottom: 1rem;
  font-size: 0.85rem;
}
.split-child-banner {
  background: #eef0fb;
  border-left: 4px solid var(--color-accent-purple, #7f77dd);
  color: #2a2a4a;
}
.split-parent-banner {
  background: #e1f5ee;
  border-left: 4px solid var(--color-teal-accent, #5dcaa5);
  color: #0f4030;
}
.dark-theme .split-child-banner {
  background: #1f2342;
  color: #cbcae8;
}
.dark-theme .split-parent-banner {
  background: #15302a;
  color: #b8e0d0;
}
.banner-icon {
  font-size: 1rem;
  flex-shrink: 0;
}
.split-child-banner .banner-icon {
  color: var(--color-accent-purple, #7f77dd);
}
.split-parent-banner .banner-icon {
  color: var(--color-teal-accent, #5dcaa5);
}
.banner-text {
  flex: 1 1 auto;
  min-width: 0;
}
.banner-title {
  font-weight: 600;
  font-size: 0.85rem;
  line-height: 1.2;
}
.banner-subtitle {
  font-size: 0.75rem;
  opacity: 0.8;
  margin-top: 2px;
}
.banner-parent-name {
  font-weight: 600;
}
.banner-link {
  flex-shrink: 0;
  background: transparent;
  border: 1px solid currentColor;
  color: inherit;
  border-radius: var(--radius-pill);
  padding: 4px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
}
.banner-link:hover {
  background: rgba(255, 255, 255, 0.5);
}
.dark-theme .banner-link:hover {
  background: rgba(255, 255, 255, 0.08);
}

.et-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  margin-bottom: 1.5rem;
  background: #f0f0f4;
  border-radius: var(--radius-pill);
  padding: 4px;
}
.dark-theme .et-tabs {
  background: #1a2535;
}
.et-tab {
  border: none;
  border-radius: var(--radius-pill);
  padding: 0.6rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: #888;
  transition: all 0.2s ease;
}
.et-tab.active {
  background: linear-gradient(135deg, #7f77dd, #5dcaa5);
  color: white;
  box-shadow: 0 4px 12px rgba(127, 119, 221, 0.3);
}
.et-tab:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.et-amount-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.25rem 0 1rem;
  animation: slideUp 0.4s ease both;
}
.et-amount-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #aaa;
  margin-bottom: 4px;
}
.et-amount-value {
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #004751;
}
.dark-theme .et-amount-value {
  color: white;
}
.amount-income {
  color: #1d9e75 !important;
}
.amount-neutral {
  color: #004751;
}
.dark-theme .amount-neutral {
  color: white;
}
.et-info-card {
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}
.et-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f0f0f0;
}
.dark-theme .et-info-row {
  border-bottom-color: #2a3a4e;
}
.et-info-row:last-child {
  border-bottom: none;
}
.et-info-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #888;
}
.et-info-value {
  font-size: 0.82rem;
  font-weight: 500;
  color: #213547;
  text-align: right;
  max-width: 60%;
}
.dark-theme .et-info-value {
  color: #d4d2d2;
}
.et-flag {
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
}
.flag-income {
  background: #e1f5ee;
  color: #0f6e56;
}
.ud-hint {
  font-weight: 400;
  color: #aaa;
  font-size: 0.72rem;
}

.delete-btn {
  border: none;
  border-radius: 50px;
  padding: 0.75rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  background: white;
  color: #d85a30;
  border: 2px solid #d85a30;
  cursor: pointer;
  transition:
    background 0.15s,
    transform 0.15s;
}
.delete-btn:hover {
  background: #faece7;
  transform: translateY(-1px);
}
.delete-btn:active {
  transform: scale(0.97);
}
.delete-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.dark-theme .delete-btn {
  background: transparent;
  color: #f09595;
  border-color: #f09595;
}

.rule-exists-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.6rem 0.9rem;
  background: #e1f5ee;
  color: #0f6e56;
  border-radius: 10px;
  font-size: 0.82rem;
  font-weight: 600;
}
.dark-theme .rule-exists-pill {
  background: rgba(29, 158, 117, 0.15);
  color: #5dcaa5;
}

.create-rule-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 0.7rem 1rem;
  background: var(--purple-50, #eeedfe);
  color: var(--color-accent-purple, #7f77dd);
  border: 1.5px dashed var(--color-accent-purple, #7f77dd);
  border-radius: 10px;
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-fast);
}
.create-rule-btn:hover {
  background: var(--color-accent-purple, #7f77dd);
  color: white;
  transform: translateY(-1px);
}
.dark-theme .create-rule-btn {
  background: transparent;
  border-color: #afa9ec;
  color: #afa9ec;
}
.dark-theme .create-rule-btn:hover {
  background: #afa9ec;
  color: #1a2535;
}

.split-intro {
  margin-bottom: 1rem;
}
.split-intro-text {
  font-size: 0.85rem;
  color: #555;
  line-height: 1.45;
  margin: 0;
}
.dark-theme .split-intro-text {
  color: #b9bcc4;
}

.quick-split-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 1.25rem;
}
.quick-split-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #888;
  margin-right: 4px;
}
.quick-split-btn {
  background: white;
  border: 2px solid var(--color-accent-purple, #7f77dd);
  color: var(--color-accent-purple, #7f77dd);
  border-radius: var(--radius-pill);
  padding: 0.4rem 0.9rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  min-width: 44px;
  transition:
    background 0.15s,
    transform 0.15s;
}
.quick-split-btn:hover:not(:disabled) {
  background: var(--purple-50, #eeedfe);
  transform: translateY(-1px);
}
.quick-split-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.quick-split-btn-secondary {
  border-color: var(--color-teal-accent, #5dcaa5);
  color: var(--color-teal-accent, #5dcaa5);
  font-size: 0.75rem;
  padding: 0.4rem 0.85rem;
}
.quick-split-btn-secondary:hover:not(:disabled) {
  background: #e1f5ee;
}
.dark-theme .quick-split-btn {
  background: transparent;
  color: #aaa3f0;
  border-color: #aaa3f0;
}
.dark-theme .quick-split-btn-secondary {
  color: #7fdcb9;
  border-color: #7fdcb9;
}

.splits-list {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-bottom: 1rem;
}
.split-card {
  padding: 1rem;
  position: relative;
  animation: cardSlideUp 0.3s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}
.split-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.6rem;
}
.split-card-title {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-accent-purple, #7f77dd);
}
.split-remove-btn {
  background: transparent;
  border: none;
  color: #aaa;
  font-size: 1rem;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition:
    background 0.15s,
    color 0.15s;
}
.split-remove-btn:hover:not(:disabled) {
  background: #faece7;
  color: #d85a30;
}
.split-remove-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.add-split-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  background: transparent;
  border: 2px dashed #cfd0dc;
  color: #888;
  border-radius: var(--radius-md);
  padding: 0.75rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 1.25rem;
  transition:
    border-color 0.15s,
    color 0.15s,
    background 0.15s;
}
.add-split-btn:hover {
  border-color: var(--color-accent-purple, #7f77dd);
  color: var(--color-accent-purple, #7f77dd);
  background: var(--purple-50, #eeedfe);
}
.dark-theme .add-split-btn {
  border-color: #2a3a4e;
  color: #888;
}
.dark-theme .add-split-btn:hover {
  border-color: #aaa3f0;
  color: #aaa3f0;
  background: rgba(127, 119, 221, 0.08);
}

.split-totals {
  background: white;
  border-radius: var(--radius-md);
  padding: 0.85rem 1rem;
  margin-bottom: 1rem;
  border: 1px solid #f0f0f0;
}
.dark-theme .split-totals {
  background: #212e3e;
  border-color: #2a3a4e;
}
.split-totals-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.3rem 0;
}
.split-totals-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #888;
}
.split-totals-value {
  font-size: 0.85rem;
  font-weight: 600;
  color: #213547;
  font-variant-numeric: tabular-nums;
}
.dark-theme .split-totals-value {
  color: #d4d2d2;
}
.split-totals-remaining {
  border-top: 1px solid #f0f0f0;
  margin-top: 0.3rem;
  padding-top: 0.55rem;
}
.dark-theme .split-totals-remaining {
  border-top-color: #2a3a4e;
}
.remaining-balanced .split-totals-value,
.remaining-balanced .split-totals-label {
  color: #1d9e75;
  font-weight: 700;
}
.remaining-off .split-totals-value,
.remaining-off .split-totals-label {
  color: #d85a30;
  font-weight: 700;
}

.split-validation {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff5f0;
  border-left: 3px solid #d85a30;
  color: #8a3a18;
  padding: 0.7rem 0.85rem;
  border-radius: 6px;
  font-size: 0.8rem;
  margin-bottom: 1rem;
}
.dark-theme .split-validation {
  background: #2e1a14;
  color: #f09595;
}
.validation-icon {
  flex-shrink: 0;
  color: #d85a30;
}

.ud-submit:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  filter: grayscale(0.3);
}

.split-children-loading {
  text-align: center;
  color: #888;
  padding: 1rem;
  font-size: 0.85rem;
}

.split-children-list {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 1.25rem;
}

.split-child-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.85rem 1rem;
  cursor: pointer;
  transition:
    transform 0.15s,
    box-shadow 0.15s;
}
.split-child-row:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-md, 0 4px 12px rgba(0, 0, 0, 0.08));
}
.split-child-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f8f8f8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 14px;
}
.dark-theme .split-child-icon {
  background: #1a2535;
}
.split-child-info {
  flex: 1 1 auto;
  min-width: 0;
}
.split-child-name {
  font-weight: 600;
  font-size: 0.85rem;
  color: #213547;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark-theme .split-child-name {
  color: #fff;
}
.split-child-category {
  font-size: 0.72rem;
  color: #888;
  margin-top: 2px;
}
.split-child-amount {
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--color-primary, #004751);
  font-variant-numeric: tabular-nums;
}
.dark-theme .split-child-amount {
  color: #fff;
}

.split-actions-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 1rem;
}
.unsplit-btn,
.resplit-btn {
  border-radius: var(--radius-pill);
  padding: 0.7rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition:
    background 0.15s,
    transform 0.15s;
}
.unsplit-btn {
  background: white;
  color: #d85a30;
  border: 2px solid #d85a30;
}
.unsplit-btn:hover {
  background: #faece7;
  transform: translateY(-1px);
}
.dark-theme .unsplit-btn {
  background: transparent;
  color: #f09595;
  border-color: #f09595;
}
.resplit-btn {
  background: linear-gradient(135deg, #7f77dd, #5dcaa5);
  color: white;
  border: none;
}
.resplit-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(127, 119, 221, 0.3);
}
</style>
